import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft, Play, Upload, Plus, Save, Download,
  Image as ImageIcon, Settings2, Share2, Music, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import TierEditor from "../components/TierEditor";
import BulkImportDialog from "../components/BulkImportDialog";
import ShareDialog from "../components/ShareDialog";
import SortableSlideItem from "../components/SortableSlideItem";
import { SlideCanvas } from "../components/SlideCanvas";
import SlideInspector from "../components/editor/SlideInspector";
import PDFInspector from "../components/editor/PDFInspector";
import ExportDialog from "../components/editor/ExportDialog";
import PdfImportDialog from "../components/editor/PdfImportDialog";
import ImageEditorDialog from "../components/editor/ImageEditorDialog";
import CaptionFallbackInspector from "../components/editor/CaptionFallbackInspector";
import BrokenImagesDialog from "../components/editor/BrokenImagesDialog";
import { resolveCaption } from "../lib/captionResolver";

import { BACKEND } from "../lib/backend";

export default function JournalEditorPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [journal, setJournal] = useState(null);
  const [slides, setSlides] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [meta, setMeta] = useState({ title: "", description: "", base_slide_duration: 5, transition: "fade" });
  const [selectedId, setSelectedId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [pdfPreset, setPdfPreset] = useState("letter");
  const [customW, setCustomW] = useState(8.5);
  const [customH, setCustomH] = useState(11);
  const [slidesPerPage, setSlidesPerPage] = useState(1);
  const [marginPct, setMarginPct] = useState(5);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState("");
  const [companies, setCompanies] = useState([]);
  const [uploadingMusic, setUploadingMusic] = useState(false);
  const [bgFields, setBgFields] = useState({
    slide_background_color: "#0F0F11",
    slide_background_image_url: "",
    pdf_page_background_color: "#FFFFFF",
    pdf_page_background_image_url: "",
  });
  const [pdfPrefs, setPdfPrefs] = useState({
    pdf_header_text: "",
    pdf_footer_text: "{page} / {total}",
    pdf_cover_enabled: false,
    pdf_cover_subtitle: "",
    pdf_cover_image_url: "",
    pdf_toc_enabled: false,
    pdf_slides_per_page: 1,
    pdf_margin_pct: 5,
    pdf_page_number_start: 1,
  });
  const [captionPrefs, setCaptionPrefs] = useState({
    caption_fallback_enabled: false,
    caption_templates: [],
    caption_variables: {},
  });
  const [previewPages, setPreviewPages] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewMeta, setPreviewMeta] = useState({ total_pages: 0, truncated: false });

  const [pdfImportOpen, setPdfImportOpen] = useState(false);
  const [pdfImportTargetId, setPdfImportTargetId] = useState(null); // slide id receiving page 1
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorTargetSlideId, setEditorTargetSlideId] = useState(null);

  const [brokenCount, setBrokenCount] = useState(0);
  const [brokenDialogOpen, setBrokenDialogOpen] = useState(false);

  const scanBroken = async () => {
    try {
      const { data } = await api.get(`/journals/${id}/broken-images`);
      setBrokenCount(data.count || 0);
    } catch {
      // Silent: the banner is opportunistic, not critical.
    }
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const load = async () => {
    try {
      const { data } = await api.get(`/journals/${id}`);
      setJournal(data);
      setSlides([...(data.slides || [])].sort((a, b) => (a.order || 0) - (b.order || 0)));
      setTiers(data.tiers || []);
      setMeta({
        title: data.title,
        description: data.description || "",
        base_slide_duration: data.base_slide_duration || 5,
        transition: data.transition || "fade",
      });
      setBgFields({
        slide_background_color: data.slide_background_color || "#0F0F11",
        slide_background_image_url: data.slide_background_image_url || "",
        pdf_page_background_color: data.pdf_page_background_color || "#FFFFFF",
        pdf_page_background_image_url: data.pdf_page_background_image_url || "",
      });
      setPdfPrefs({
        pdf_header_text: data.pdf_header_text || "",
        pdf_footer_text: data.pdf_footer_text ?? "{page} / {total}",
        pdf_cover_enabled: !!data.pdf_cover_enabled,
        pdf_cover_subtitle: data.pdf_cover_subtitle || "",
        pdf_cover_image_url: data.pdf_cover_image_url || "",
        pdf_toc_enabled: !!data.pdf_toc_enabled,
        pdf_slides_per_page: data.pdf_slides_per_page || 1,
        pdf_margin_pct: data.pdf_margin_pct ?? 5,
        pdf_page_number_start: data.pdf_page_number_start ?? 1,
      });
      setCaptionPrefs({
        caption_fallback_enabled: !!data.caption_fallback_enabled,
        caption_templates: Array.isArray(data.caption_templates) ? data.caption_templates : [],
        caption_variables: data.caption_variables && typeof data.caption_variables === "object"
          ? data.caption_variables
          : {},
      });
      // Prefill export controls from journal defaults
      setSlidesPerPage(data.pdf_slides_per_page || 1);
      setMarginPct(data.pdf_margin_pct ?? 5);
      if (!selectedId && data.slides?.length) setSelectedId(data.slides[0].id);
    } catch (e) {
      toast.error("Failed to load journal");
      nav("/dashboard");
    }
  };

  useEffect(() => {
    load();
    api.get("/companies").then(({ data }) => setCompanies(data)).catch(() => {});
    scanBroken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const selected = useMemo(() => slides.find((s) => s.id === selectedId) || null, [slides, selectedId]);
  const tierMap = useMemo(() => Object.fromEntries(tiers.map((t) => [t.id, t])), [tiers]);

  const saveAll = async () => {
    setSaving(true);
    try {
      await api.put(`/journals/${id}`, {
        title: meta.title,
        description: meta.description,
        base_slide_duration: parseFloat(meta.base_slide_duration),
        tiers,
        transition: meta.transition,
        ...bgFields,
        ...pdfPrefs,
        ...captionPrefs,
      });
      await api.put(`/journals/${id}/slides`, { slides });
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const addBlankSlide = () => {
    const id2 = (Math.random().toString(36) + Date.now().toString(36)).slice(2);
    const next = [...slides, {
      id: id2,
      company_name: "New Company",
      caption: "",
      image_url: "",
      tier_id: tiers[0]?.id || null,
      order: slides.length,
    }];
    setSlides(next);
    setSelectedId(id2);
  };

  const updateSlide = (patch) => {
    setSlides((arr) => arr.map((s) => (s.id === selectedId ? { ...s, ...patch } : s)));
  };

  const removeSlide = (sid) => {
    setSlides((arr) => arr.filter((s) => s.id !== sid).map((s, i) => ({ ...s, order: i })));
    if (sid === selectedId) setSelectedId(null);
  };

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSlides((arr) => {
      const oldIdx = arr.findIndex((s) => s.id === active.id);
      const newIdx = arr.findIndex((s) => s.id === over.id);
      if (oldIdx < 0 || newIdx < 0) return arr;
      const moved = arrayMove(arr, oldIdx, newIdx);
      return moved.map((s, i) => ({ ...s, order: i }));
    });
  };

  const onMusicUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingMusic(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(`/journals/${id}/music`, fd);
      setJournal((j) => ({ ...j, music_url: data.music_url }));
      toast.success("Music attached");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Music upload failed");
    } finally {
      setUploadingMusic(false);
    }
  };

  const removeMusic = async () => {
    try {
      await api.delete(`/journals/${id}/music`);
      setJournal((j) => ({ ...j, music_url: "" }));
      toast.success("Music removed");
    } catch {
      toast.error("Failed");
    }
  };

  const onSlideLogoUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !selected) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/uploads/image", fd);
      updateSlide({ image_url: data.url });
      toast.success("Logo updated");
    } catch (err) {
      toast.error("Upload failed");
    }
  };

  const pickFromCompany = (companyId) => {
    if (!selected) return;
    const c = companies.find((x) => x.id === companyId);
    if (!c) return;
    updateSlide({
      company_id: c.id,
      company_name: c.name,
      caption: c.caption || selected.caption || "",
      image_url: c.logo_url || selected.image_url || "",
    });
  };

  // ----- Exports -----
  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const exportImages = async () => {
    setExporting("images");
    try {
      const res = await api.get(`/journals/${id}/export/images`, { responseType: "blob" });
      downloadBlob(res.data, `${meta.title || "journal"}_slides.zip`);
    } catch {
      toast.error("Image export failed");
    } finally {
      setExporting("");
    }
  };

  const exportPdf = async () => {
    setExporting("pdf");
    try {
      const body = {
        preset: pdfPreset,
        slides_per_page: slidesPerPage,
        margin_pct: marginPct,
      };
      if (pdfPreset === "custom") {
        body.custom_width_in = parseFloat(customW);
        body.custom_height_in = parseFloat(customH);
      }
      const res = await api.post(`/journals/${id}/export/pdf`, body, { responseType: "blob" });
      downloadBlob(res.data, `${meta.title || "journal"}.pdf`);
    } catch {
      toast.error("PDF export failed");
    } finally {
      setExporting("");
    }
  };

  const fetchPreview = async () => {
    setPreviewLoading(true);
    try {
      const body = {
        preset: pdfPreset,
        slides_per_page: slidesPerPage,
        margin_pct: marginPct,
      };
      if (pdfPreset === "custom") {
        body.custom_width_in = parseFloat(customW);
        body.custom_height_in = parseFloat(customH);
      }
      const { data } = await api.post(`/journals/${id}/export/pdf/preview`, body);
      setPreviewPages(data.pages || []);
      setPreviewMeta({ total_pages: data.total_pages, truncated: data.truncated });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  const uploadBg = async (field, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/image", fd);
      // bgFields fields start with slide_/pdf_page_; pdfPrefs fields start with pdf_cover_image_url
      if (field === "pdf_cover_image_url") {
        setPdfPrefs((p) => ({ ...p, pdf_cover_image_url: data.url }));
      } else {
        setBgFields((b) => ({ ...b, [field]: data.url }));
      }
      toast.success("Background image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
  };

  const uploadSlideBg = async (file) => {
    if (!file || !selected) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/image", fd);
      updateSlide({ background_image_url: data.url });
      toast.success("Slide background uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
  };

  const exportMp4 = async () => {
    setExporting("mp4");
    try {
      const res = await api.get(`/journals/${id}/export/mp4`, { responseType: "blob" });
      downloadBlob(res.data, `${meta.title || "journal"}.mp4`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "MP4 export failed");
    } finally {
      setExporting("");
    }
  };

  // ----- PDF import / Image editor -----
  const openPdfImportFor = (slideId) => {
    setPdfImportTargetId(slideId);
    setPdfImportOpen(true);
  };

  const onPdfPagesRendered = (images /* [{page, url, suggested_name?, suggested_caption?}] */, info) => {
    if (!images?.length) return;
    const baseName = info?.originalName || "PDF";
    const ocrName = (im) => (im.suggested_name || "").trim();
    const ocrCaption = (im) => (im.suggested_caption || "").trim();
    setSlides((arr) => {
      const idx = arr.findIndex((s) => s.id === pdfImportTargetId);
      if (idx < 0) {
        // No target slide: append all pages as new slides, auto-filled from OCR
        const additions = images.map((im, i) => ({
          id: (Math.random().toString(36) + Date.now().toString(36)).slice(2) + i,
          company_name: ocrName(im) || `${baseName} (p${im.page})`,
          caption: ocrCaption(im),
          image_url: im.url,
          tier_id: tiers[0]?.id || null,
          order: arr.length + i,
        }));
        return [...arr, ...additions];
      }
      // First page replaces the target slide's image; auto-fill empty fields only
      const target = arr[idx];
      const first = images[0];
      const patched = {
        ...target,
        image_url: first.url,
        company_name: (target.company_name && target.company_name !== "New Company")
          ? target.company_name
          : (ocrName(first) || target.company_name || `${baseName} (p${first.page})`),
        caption: (target.caption && target.caption.trim())
          ? target.caption
          : ocrCaption(first),
      };
      const next = arr.map((s, i) => (i === idx ? patched : s));
      // Additional pages become new slides inserted after the target
      if (images.length > 1) {
        const extras = images.slice(1).map((im, i) => ({
          id: (Math.random().toString(36) + Date.now().toString(36)).slice(2) + i,
          company_name: ocrName(im) || `${target.company_name || baseName} (p${im.page})`,
          caption: ocrCaption(im),
          image_url: im.url,
          tier_id: target.tier_id || tiers[0]?.id || null,
          order: 0, // will be re-numbered
        }));
        const merged = [...next.slice(0, idx + 1), ...extras, ...next.slice(idx + 1)];
        return merged.map((s, i) => ({ ...s, order: i }));
      }
      return next;
    });
    setPdfImportTargetId(null);
  };

  const openImageEditorFor = (slideId) => {
    setEditorTargetSlideId(slideId);
    setEditorOpen(true);
  };

  const onImageEdited = (newUrl) => {
    setSlides((arr) => arr.map((s) => (s.id === editorTargetSlideId ? { ...s, image_url: newUrl } : s)));
    setEditorTargetSlideId(null);
  };

  const editorTargetSlide = useMemo(
    () => slides.find((s) => s.id === editorTargetSlideId) || null,
    [slides, editorTargetSlideId],
  );

  if (!journal) {
    return <div className="min-h-screen bg-zinc-950 text-zinc-500 grid place-items-center">Loading editor…</div>;
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 flex flex-col">
      {/* Top bar */}
      <header className="flex items-center justify-between gap-4 border-b border-zinc-800 px-4 h-14 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button data-testid="back-to-dashboard" onClick={() => nav("/dashboard")} className="text-zinc-400 hover:text-zinc-50 p-1.5 rounded hover:bg-zinc-900">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Input
            data-testid="journal-title-input"
            value={meta.title}
            onChange={(e) => setMeta({ ...meta, title: e.target.value })}
            className="bg-transparent border-none focus-visible:ring-0 px-0 text-lg font-display font-semibold w-80"
          />
          <div className="label-eyebrow">{slides.length} slides</div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="bulk-import-btn"
            variant="outline"
            onClick={() => setImportOpen(true)}
            className="border-zinc-800 hover:bg-zinc-900 text-zinc-50"
          >
            <Upload className="w-4 h-4 mr-2" /> Bulk Import
          </Button>
          <Button
            data-testid="export-btn"
            variant="outline"
            onClick={() => setExportOpen(true)}
            className="border-zinc-800 hover:bg-zinc-900 text-zinc-50"
          >
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
          <Button
            data-testid="share-btn"
            variant="outline"
            onClick={() => setShareOpen(true)}
            className="border-zinc-800 hover:bg-zinc-900 text-zinc-50"
          >
            <Share2 className="w-4 h-4 mr-2" /> Share
          </Button>
          <Button
            data-testid="play-btn"
            onClick={async () => { await saveAll(); nav(`/journals/${id}/play`); }}
            className="bg-blue-600 hover:bg-blue-500"
          >
            <Play className="w-4 h-4 mr-2" /> Play
          </Button>
          <Button
            data-testid="save-btn"
            onClick={saveAll}
            disabled={saving}
            variant="secondary"
            className="bg-zinc-800 hover:bg-zinc-700 text-zinc-50"
          >
            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </header>

      {brokenCount > 0 && (
        <div
          data-testid="broken-images-banner"
          className="border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 flex items-center gap-3"
        >
          <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0" />
          <div className="text-xs text-amber-100 flex-1">
            <span className="font-semibold">{brokenCount} slide{brokenCount === 1 ? "" : "s"}</span> reference logos
            that are no longer on the server. Exports will show a placeholder.
          </div>
          <Button
            data-testid="open-broken-dialog-btn"
            variant="outline"
            size="sm"
            onClick={() => setBrokenDialogOpen(true)}
            className="border-amber-400/40 bg-amber-400/10 hover:bg-amber-400/20 text-amber-100"
          >
            Repair
          </Button>
        </div>
      )}

      <div className="grid grid-cols-12 flex-1 min-h-0">
        {/* Left: Slides strip */}
        <aside className="col-span-3 border-r border-zinc-800 flex flex-col min-h-0">
          <div className="flex items-center justify-between px-4 h-10 border-b border-zinc-800">
            <div className="label-eyebrow">Slides</div>
            <button data-testid="add-slide-btn" onClick={addBlankSlide} className="text-zinc-400 hover:text-zinc-50 p-1 rounded hover:bg-zinc-800">
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {slides.length === 0 && (
              <div className="text-xs text-zinc-500 text-center py-12">
                No slides yet.<br />Use Bulk Import or +.
              </div>
            )}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={slides.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                {slides.map((s, i) => (
                  <SortableSlideItem
                    key={s.id}
                    slide={s}
                    index={i}
                    tier={tierMap[s.tier_id]}
                    isSelected={s.id === selectedId}
                    baseDuration={meta.base_slide_duration}
                    onSelect={() => setSelectedId(s.id)}
                    onEditImage={openImageEditorFor}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        </aside>

        {/* Center: Preview */}
        <section className="col-span-6 flex flex-col bg-zinc-950 min-h-0">
          <div className="flex-1 grid place-items-center p-8 overflow-auto">
            <div
              data-testid="slide-preview"
              className="aspect-video w-full max-w-4xl bg-black rounded-md shadow-2xl border border-zinc-800 relative overflow-hidden"
            >
              {selected ? (
                <SlideCanvas
                  slide={{
                    ...selected,
                    caption: resolveCaption(
                      selected,
                      { title: meta.title, ...captionPrefs },
                      slides.findIndex((s) => s.id === selected.id),
                    ) || selected.caption,
                  }}
                  tier={tierMap[selected.tier_id]}
                  bgColor={selected.background_color || bgFields.slide_background_color}
                  bgImage={selected.background_image_url || bgFields.slide_background_image_url}
                />
              ) : (
                <div className="absolute inset-0 grid place-items-center text-zinc-600 text-sm">
                  Select or create a slide to preview
                </div>
              )}
            </div>
          </div>
          <div className="border-t border-zinc-800 px-6 py-3 flex items-center justify-between">
            <div className="label-eyebrow">Preview · 1920 × 1080</div>
            <div className="text-xs text-zinc-500 font-mono">
              {selected && tierMap[selected.tier_id]
                ? `${(tierMap[selected.tier_id].duration_multiplier * meta.base_slide_duration).toFixed(1)}s`
                : `${meta.base_slide_duration}s`}
            </div>
          </div>
        </section>

        {/* Right: Properties */}
        <aside className="col-span-3 border-l border-zinc-800 flex flex-col min-h-0">
          <div className="px-4 h-10 border-b border-zinc-800 flex items-center gap-2">
            <Settings2 className="w-3.5 h-3.5 text-zinc-400" />
            <div className="label-eyebrow">Inspector</div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            <SlideInspector
              selected={selected}
              tiers={tiers}
              companies={companies}
              bgFields={bgFields}
              updateSlide={updateSlide}
              onSlideLogoUpload={onSlideLogoUpload}
              pickFromCompany={pickFromCompany}
              uploadSlideBg={uploadSlideBg}
              removeSlide={removeSlide}
              onOpenPdfImport={openPdfImportFor}
              onOpenImageEditor={openImageEditorFor}
            />

            <div className="border-t border-zinc-800 pt-4 space-y-4">
              <div className="label-eyebrow">Journal settings</div>
              <div className="space-y-2">
                <Label className="label-eyebrow">Description</Label>
                <Textarea
                  data-testid="journal-description"
                  value={meta.description}
                  onChange={(e) => setMeta({ ...meta, description: e.target.value })}
                  rows={2}
                  className="bg-zinc-950 border-zinc-800"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-eyebrow">Base slide duration (s)</Label>
                <Input
                  data-testid="journal-base-duration"
                  type="number"
                  min={1}
                  step={0.5}
                  value={meta.base_slide_duration}
                  onChange={(e) => setMeta({ ...meta, base_slide_duration: parseFloat(e.target.value || 1) })}
                  className="bg-zinc-950 border-zinc-800"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-eyebrow">Transition</Label>
                <Select value={meta.transition} onValueChange={(v) => setMeta({ ...meta, transition: v })}>
                  <SelectTrigger data-testid="journal-transition-select" className="bg-zinc-950 border-zinc-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                    <SelectItem value="cut">Cut (instant)</SelectItem>
                    <SelectItem value="fade">Fade</SelectItem>
                    <SelectItem value="slide">Slide</SelectItem>
                    <SelectItem value="zoom">Zoom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-eyebrow flex items-center gap-1">
                  <Music className="w-3 h-3" /> Background music
                </Label>
                {journal.music_url ? (
                  <div className="bg-zinc-950 border border-zinc-800 rounded-md p-2 flex items-center gap-2">
                    <audio controls src={`${BACKEND}${journal.music_url}`} className="flex-1 h-8" />
                    <button
                      type="button"
                      data-testid="remove-music-btn"
                      onClick={removeMusic}
                      className="text-xs text-red-400 hover:text-red-300 shrink-0"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <Input
                    data-testid="music-upload"
                    type="file"
                    accept="audio/*"
                    onChange={onMusicUpload}
                    className="bg-zinc-950 border-zinc-800 text-xs"
                  />
                )}
                {uploadingMusic && <p className="text-xs text-zinc-500">Uploading…</p>}
                <p className="text-xs text-zinc-500">Plays during the slideshow and is mixed into the MP4 export.</p>
              </div>
              <TierEditor tiers={tiers} onChange={setTiers} />

              <div className="space-y-3 border-t border-zinc-800 pt-4">
                <div className="label-eyebrow">Backgrounds</div>
                <div className="space-y-2">
                  <Label className="label-eyebrow">Slide background</Label>
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="slide-bg-color"
                      type="color"
                      value={bgFields.slide_background_color || "#0F0F11"}
                      onChange={(e) => setBgFields({ ...bgFields, slide_background_color: e.target.value })}
                      className="w-8 h-8 bg-transparent border border-zinc-800 rounded cursor-pointer"
                    />
                    <Input
                      data-testid="slide-bg-color-text"
                      value={bgFields.slide_background_color || ""}
                      onChange={(e) => setBgFields({ ...bgFields, slide_background_color: e.target.value })}
                      className="bg-zinc-950 border-zinc-800 h-8 text-xs font-mono flex-1"
                      placeholder="#0F0F11"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    {bgFields.slide_background_image_url ? (
                      <>
                        <img
                          src={`${BACKEND}${bgFields.slide_background_image_url}`}
                          alt=""
                          className="w-10 h-10 rounded object-cover border border-zinc-800"
                        />
                        <button
                          type="button"
                          data-testid="clear-slide-bg-image"
                          onClick={() => setBgFields({ ...bgFields, slide_background_image_url: "" })}
                          className="text-xs text-red-400 hover:text-red-300"
                        >
                          Remove image
                        </button>
                      </>
                    ) : (
                      <Input
                        data-testid="slide-bg-image"
                        type="file"
                        accept="image/*"
                        onChange={(e) => uploadBg("slide_background_image_url", e.target.files?.[0])}
                        className="bg-zinc-950 border-zinc-800 text-xs"
                      />
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="label-eyebrow">PDF page background</Label>
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="page-bg-color"
                      type="color"
                      value={bgFields.pdf_page_background_color || "#FFFFFF"}
                      onChange={(e) => setBgFields({ ...bgFields, pdf_page_background_color: e.target.value })}
                      className="w-8 h-8 bg-transparent border border-zinc-800 rounded cursor-pointer"
                    />
                    <Input
                      data-testid="page-bg-color-text"
                      value={bgFields.pdf_page_background_color || ""}
                      onChange={(e) => setBgFields({ ...bgFields, pdf_page_background_color: e.target.value })}
                      className="bg-zinc-950 border-zinc-800 h-8 text-xs font-mono flex-1"
                      placeholder="#FFFFFF"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    {bgFields.pdf_page_background_image_url ? (
                      <>
                        <img
                          src={`${BACKEND}${bgFields.pdf_page_background_image_url}`}
                          alt=""
                          className="w-10 h-10 rounded object-cover border border-zinc-800"
                        />
                        <button
                          type="button"
                          data-testid="clear-page-bg-image"
                          onClick={() => setBgFields({ ...bgFields, pdf_page_background_image_url: "" })}
                          className="text-xs text-red-400 hover:text-red-300"
                        >
                          Remove image
                        </button>
                      </>
                    ) : (
                      <Input
                        data-testid="page-bg-image"
                        type="file"
                        accept="image/*"
                        onChange={(e) => uploadBg("pdf_page_background_image_url", e.target.files?.[0])}
                        className="bg-zinc-950 border-zinc-800 text-xs"
                      />
                    )}
                  </div>
                </div>
                <p className="text-xs text-zinc-500">Save to apply across slideshow, MP4 and PDF exports.</p>
              </div>

              <PDFInspector
                pdfPrefs={pdfPrefs}
                setPdfPrefs={setPdfPrefs}
                setSlidesPerPage={setSlidesPerPage}
                setMarginPct={setMarginPct}
                uploadBg={uploadBg}
              />

              <CaptionFallbackInspector
                captionPrefs={captionPrefs}
                setCaptionPrefs={setCaptionPrefs}
                journalId={id}
              />
            </div>
          </div>
        </aside>
      </div>

      <BulkImportDialog open={importOpen} onOpenChange={setImportOpen} journalId={id} onImported={load} />
      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} journal={journal} onUpdate={setJournal} />

      <PdfImportDialog
        open={pdfImportOpen}
        onOpenChange={(v) => { setPdfImportOpen(v); if (!v) setPdfImportTargetId(null); }}
        mode={pdfImportTargetId ? "single" : "bulk"}
        onPagesRendered={onPdfPagesRendered}
      />

      <ImageEditorDialog
        open={editorOpen}
        onOpenChange={(v) => { setEditorOpen(v); if (!v) setEditorTargetSlideId(null); }}
        imageUrl={editorTargetSlide?.image_url || null}
        onSaved={onImageEdited}
      />

      <ExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        pdfPreset={pdfPreset}
        setPdfPreset={setPdfPreset}
        customW={customW}
        setCustomW={setCustomW}
        customH={customH}
        setCustomH={setCustomH}
        slidesPerPage={slidesPerPage}
        setSlidesPerPage={setSlidesPerPage}
        marginPct={marginPct}
        setMarginPct={setMarginPct}
        exporting={exporting}
        exportMp4={exportMp4}
        exportImages={exportImages}
        exportPdf={exportPdf}
        previewPages={previewPages}
        previewMeta={previewMeta}
        previewLoading={previewLoading}
        fetchPreview={fetchPreview}
      />

      <BrokenImagesDialog
        open={brokenDialogOpen}
        onOpenChange={setBrokenDialogOpen}
        journalId={id}
        onChanged={async () => { await load(); await scanBroken(); }}
      />
    </div>
  );
}
